import { z } from 'zod';

export const ITEM_CATEGORIES = [
  'UNKNOWN',
  'WALLET',
  'PHONE',
  'KEYS',
  'BAG',
  'DOCUMENT',
  'JEWELLERY',
  'ELECTRONICS',
  'CLOTHING',
  'OTHER',
] as const;

export const ITEM_COLORS = [
  'UNKNOWN',
  'BLACK',
  'WHITE',
  'GREY',
  'BROWN',
  'RED',
  'ORANGE',
  'YELLOW',
  'GREEN',
  'BLUE',
  'PURPLE',
  'PINK',
  'MULTICOLOR',
  'OTHER',
] as const;

const TimestampSchema = z.iso.datetime({ offset: true });
const DaySchema = z.iso.date();
const TrimmedText = (max: number) => z.string().trim().min(1).max(max);
const OptionalText = (max: number) => z.string().trim().min(1).max(max).optional();

export const LostDateSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('UNKNOWN') }).strict(),
  z
    .object({
      kind: z.literal('KNOWN'),
      start: DaySchema,
      end: DaySchema,
      label: OptionalText(120),
    })
    .strict()
    .refine((value) => value.start <= value.end, { message: 'Date range must be ordered' }),
]);

export const PlaceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('UNKNOWN') }).strict(),
  z
    .object({
      kind: z.literal('VENUE'),
      venueId: TrimmedText(128),
      name: TrimmedText(200),
      context: OptionalText(500),
      differentPlace: z.literal(false),
    })
    .strict(),
  z
    .object({
      kind: z.literal('GOOGLE'),
      placeId: TrimmedText(300),
      name: TrimmedText(200),
      address: TrimmedText(500),
      latitude: z.number().min(-90).max(90),
      longitude: z.number().min(-180).max(180),
      context: OptionalText(500),
      differentPlace: z.literal(true),
    })
    .strict(),
  z
    .object({
      kind: z.literal('FREE_FORM'),
      name: TrimmedText(300),
      context: OptionalText(500),
      differentPlace: z.literal(true),
    })
    .strict(),
]);

export const TranscriptEntrySchema = z
  .object({ role: z.enum(['USER', 'ASSISTANT']), text: TrimmedText(2_000) })
  .strict();

const LostItemReportV1BaseSchema = z
  .object({
    schemaVersion: z.literal('lost-item-report.v1'),
    reportId: z.uuid(),
    venueContext: z
      .object({
        venueId: TrimmedText(128),
        venueName: TrimmedText(200),
        invitationId: TrimmedText(128),
      })
      .strict(),
    createdAt: TimestampSchema,
    expiresAt: TimestampSchema,
    intake: z
      .object({
        source: z.enum(['VOICE', 'TEXT']),
        language: z.literal('en'),
        redactedTranscript: z.array(TranscriptEntrySchema).max(40),
        confidence: z.number().min(0).max(1),
        clarificationCount: z.number().int().min(0).max(2),
        extractionToolCallCount: z.number().int().min(0).max(1),
      })
      .strict(),
    item: z
      .object({
        description: TrimmedText(1_000),
        category: z.enum(ITEM_CATEGORIES),
        color: z.enum(ITEM_COLORS),
        brand: TrimmedText(120).or(z.literal('UNKNOWN')),
        distinguishingFeatures: OptionalText(1_000),
      })
      .strict(),
    lostDate: LostDateSchema,
    place: PlaceSchema,
    proof: z
      .object({
        description: OptionalText(1_000),
        photo: z
          .object({
            objectName: TrimmedText(500),
            contentType: z.enum(['image/jpeg', 'image/webp']),
            sizeBytes: z.number().int().positive().max(5_000_000),
            width: z.number().int().positive().max(2_000),
            height: z.number().int().positive().max(2_000),
            sha256: z.string().regex(/^[a-f0-9]{64}$/),
          })
          .strict()
          .optional(),
      })
      .strict(),
    contact: z
      .object({
        firstName: TrimmedText(100),
        lastName: TrimmedText(100),
        email: z.email().max(320),
        phone: OptionalText(40),
        preferredChannel: z.enum(['EMAIL', 'PHONE']),
      })
      .strict()
      .refine((value) => value.preferredChannel !== 'PHONE' || Boolean(value.phone), {
        message: 'Phone is required for phone contact',
        path: ['phone'],
      }),
    legal: z
      .object({
        tosAccepted: z.literal(true),
        tosVersion: TrimmedText(80),
        tosAcceptedAt: TimestampSchema,
        privacyAccepted: z.literal(true),
        privacyNoticeVersion: TrimmedText(80),
        privacyAcceptedAt: TimestampSchema,
      })
      .strict(),
    funnel: z
      .object({
        startedAt: TimestampSchema,
        submittedAt: TimestampSchema,
        totalDurationMs: z.number().int().min(0).max(86_400_000),
        voiceDurationMs: z.number().int().min(0).max(1_200_000).optional(),
        usedTextFallback: z.boolean(),
        mandatoryFieldCorrections: z.number().int().min(0).max(20),
      })
      .strict(),
  })
  .strict();

export const LostItemReportV1Schema = LostItemReportV1BaseSchema.superRefine((report, context) => {
  if (report.createdAt >= report.expiresAt) {
    context.addIssue({
      code: 'custom',
      path: ['expiresAt'],
      message: 'Expiry must follow creation',
    });
  }
  const expectedCalls = report.intake.source === 'VOICE' ? 1 : 0;
  if (report.intake.extractionToolCallCount !== expectedCalls) {
    context.addIssue({
      code: 'custom',
      path: ['intake', 'extractionToolCallCount'],
      message: `Expected ${expectedCalls} extraction tool calls`,
    });
  }
});

export type LostItemReportV1 = z.infer<typeof LostItemReportV1Schema>;
export type LostDate = z.infer<typeof LostDateSchema>;
export type ReportPlace = z.infer<typeof PlaceSchema>;
export type ItemCategory = (typeof ITEM_CATEGORIES)[number];
export type ItemColor = (typeof ITEM_COLORS)[number];

export const ReportSubmissionSchema = LostItemReportV1BaseSchema.omit({
  reportId: true,
  createdAt: true,
  expiresAt: true,
  venueContext: true,
  proof: true,
}).extend({
  proof: z.object({ description: OptionalText(1_000) }).strict(),
});

export type ReportSubmission = z.infer<typeof ReportSubmissionSchema>;
